import { apiRequest } from "./client";
import { MarketRecommendation, MarketFixture, MarketHubView } from "@/types/marketHub";
import { PrematchFixture, PrematchMarket } from "@/types/api";
import { fixtureKeys, fixtureMatches } from "@/presentation/fixtureIdentity";

const numOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null));
const strOrNull = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);

function normRec(raw: any): MarketRecommendation | null {
  if (!raw || typeof raw !== "object") return null;
  const code = strOrNull(raw.market_code ?? raw.code ?? raw.market);
  if (!code) return null;
  return {
    market_code: code,
    market_name: strOrNull(raw.market_name ?? raw.name ?? raw.label),
    probability: numOrNull(raw.probability ?? raw.model_probability ?? raw.prob),
    reference_odd: numOrNull(raw.reference_odd ?? raw.ref_odd ?? raw.odd ?? raw.odds),
    edge: numOrNull(raw.edge),
    score: numOrNull(raw.score),
    risk: strOrNull(raw.risk ?? raw.risk_label),
    tier: strOrNull(raw.tier),
    reason: strOrNull(raw.reason ?? raw.explanation),
    is_recommendation: raw.is_recommendation !== false,
    event_id: raw.event_id ?? raw.signal_id ?? raw.id ?? null,
    signal_minute: numOrNull(raw.signal_minute ?? raw.minute),
  };
}

function normList(raw: any): MarketRecommendation[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normRec).filter(Boolean) as MarketRecommendation[];
}

function normFixture(raw: any): MarketFixture | null {
  if (!raw || typeof raw !== "object") return null;
  const keys = fixtureKeys(raw);
  const fid = strOrNull(raw.fixture_id ?? raw.fixtureId ?? raw.id) ?? (keys.length ? keys[0] : null);
  if (!fid) return null;
  return {
    fixture_id: fid,
    keys: keys.length ? keys : [fid],
    home: strOrNull(raw.home ?? raw.home_team),
    away: strOrNull(raw.away ?? raw.away_team),
    kickoff: strOrNull(raw.kickoff ?? raw.fixture_date),
    country: strOrNull(raw.country),
    league: strOrNull(raw.league),
    metrics: raw.metrics && typeof raw.metrics === "object" ? raw.metrics : null,
    profile: raw.profile ?? null,
    primary: normRec(raw.primary ?? raw.main ?? null),
    alternatives: normList(raw.alternatives ?? raw.alts ?? []),
    watching: normList(raw.watching ?? raw.watch ?? []),
    alternative_count: numOrNull(raw.alternative_count) ?? undefined,
    watching_count: numOrNull(raw.watching_count) ?? undefined,
  };
}

function collectFixtures(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  const out: any[] = [];
  for (const k of ["fixtures", "items", "data", "prematch", "live", "results"]) {
    if (Array.isArray(raw[k])) out.push(...raw[k]);
  }
  if (out.length) return out;
  // map shape: { "fs:123": {...}, "456": {...} }
  const vals = Object.values(raw).filter((v) => v && typeof v === "object" && ("primary" in (v as any) || "alternatives" in (v as any) || "watching" in (v as any)));
  return vals as any[];
}

export async function fetchMarketHub(): Promise<MarketHubView> {
  const raw = await apiRequest<any>(`/market-hub-v1`, { auth: true, retries: 1 });
  const fixtures = collectFixtures(raw).map(normFixture).filter(Boolean) as MarketFixture[];
  // Canonical PREMATCH inventory — the product's render list source of truth.
  const prematchRaw = Array.isArray(raw?.prematch) ? raw.prematch : [];
  const prematch = (prematchRaw.map(normFixture).filter(Boolean) as MarketFixture[]);
  // Canonical CUSTOMER-LIVE set — owns whether a live signal card may exist.
  const liveRaw = Array.isArray(raw?.live) ? raw.live : [];
  const live = (liveRaw.map(normFixture).filter(Boolean) as MarketFixture[]);
  // Top-level entitlement snapshot + safe live marketing count (redacted plans).
  const access = raw && typeof raw === "object" && raw.access && typeof raw.access === "object" ? raw.access : null;
  const liveTeaserCount = numOrNull(raw?.live_teaser_count ?? raw?.live_teaser ?? access?.live_teaser_count);
  const liveCount = live.length || (Array.isArray(raw?.live) ? raw.live.length : fixtures.filter((f) => !String(f.fixture_id).startsWith("fs:")).length);
  let watchingCount = 0;
  for (const f of fixtures) watchingCount += f.watching_count ?? f.watching.length;
  return { fixtures, prematch, live, prematchCount: prematch.length, liveCount, watchingCount, liveTeaserCount, access };
}

// Project a canonical Market Hub prematch row onto the PrematchFixture shape the
// Prematch UI consumes. Market Hub OWNS identity/teams/kickoff/primary; legacy
// prematch (by fixture_id) may only ENRICH (extra markets, resolved history).
export function marketHubRowToFixture(hub: MarketFixture, legacy?: PrematchFixture | null): PrematchFixture {
  const m = hub.metrics || null;
  const p = hub.primary && hub.primary.is_recommendation !== false ? hub.primary : null;
  const primaryMarket: PrematchMarket | null = p
    ? {
        id: 0, created_at: "", fixture_id: hub.fixture_id, fixture_date: hub.kickoff || "",
        minutes_to_kickoff: null, country: hub.country ?? null, league: hub.league ?? null,
        home_team: hub.home ?? "", away_team: hub.away ?? "", rank: 1,
        market_code: p.market_code, market_name: p.market_name ?? null,
        model_probability: p.probability ?? null, reference_odd: p.reference_odd ?? null,
        implied_probability: null, edge: p.edge ?? null, score: p.score ?? null,
        bookmaker_count: null, status: "PENDING", final_home_goals: null, final_away_goals: null,
        won: null, resolved_at: null,
      }
    : null;
  const legacyMarkets = legacy?.markets || [];
  // Canonical primary leads; legacy may only ADD extra markets (never overwrite
  // the canonical pick — e.g. legacy UNDER_3_5 can't displace canonical OVER_1_5).
  const markets = primaryMarket
    ? [primaryMarket, ...legacyMarkets.filter((mk) => mk.market_code !== primaryMarket.market_code)]
    : legacyMarkets;
  return {
    ...(legacy || {} as any),
    fixture_key: hub.fixture_id,
    fixture_id: hub.fixture_id,
    fixture_date: hub.kickoff ?? legacy?.fixture_date ?? "",
    minutes_to_kickoff: legacy?.minutes_to_kickoff ?? null,
    country: hub.country ?? legacy?.country ?? null,
    league: hub.league ?? legacy?.league ?? null,
    home_team: hub.home ?? legacy?.home_team ?? "",
    away_team: hub.away ?? legacy?.away_team ?? "",
    status: "PENDING",
    won: null,
    markets,
    best: primaryMarket ?? legacy?.best ?? null,
    home_logo: m?.home_logo ?? legacy?.home_logo ?? null,
    away_logo: m?.away_logo ?? legacy?.away_logo ?? null,
    league_logo: m?.league_logo ?? legacy?.league_logo ?? null,
    footystats_metrics: m ?? legacy?.footystats_metrics ?? null,
    provider: "market-hub",
  };
}

// Canonical single-fixture resolver for the Prematch DETAIL page. Market Hub
// prematch OWNS existence (identical to the list); legacy only enriches by
// fixture_id and is NEVER required for the detail to exist. Returns null only
// when the fixture is in neither source.
export function resolvePrematchFixture(
  id: string,
  legacyRows: PrematchFixture[],
  prematch: MarketFixture[] | undefined | null,
): PrematchFixture | null {
  const legacyMatch = legacyRows.find((x) => String(x.fixture_id) === String(id) || fixtureMatches(x, id)) || null;
  const canonical = (prematch || []).find((r) => String(r.fixture_id) === String(id) || fixtureMatches(r, id)) || null;
  if (canonical) return marketHubRowToFixture(canonical, legacyMatch);
  return legacyMatch || null;
}
